import {useEffect, useState, useRef, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useSearchParams, useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../_gori/helpers'
import {ROUTES} from '../../../_gori/constants'
import {sanitizeHTML} from './utils/sanitize'
import {formatDate} from './utils/dateFormatter'

type BlogPost = {
  id: number
  title: string
  slug: string
  link: string
  featured_image: string | null
  excerpt?: string
  date?: string
}

type BlogResponse = {
  posts: BlogPost[]
  total_pages: number
  current_page: number
  per_page: number
  total_posts: number
}

const MAX_VISIBLE_PAGES = 7
const API_URL = process.env.REACT_APP_API_URL || ''

export function BlogPage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const activeSearch = searchParams.get('search') || ''
  const isInitialMount = useRef(true)
  const previousSearch = useRef(activeSearch)
  const abortControllerRef = useRef<AbortController | null>(null)
  const recentPostsRef = useRef<HTMLDivElement>(null)
  const [showFloatingBack, setShowFloatingBack] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  const fetchFeaturedPost = useCallback(async () => {
    if (activeSearch) return

    try {
      const res = await fetch(`${API_URL}/api/blog/posts?page=1&per_page=1`)

      if (!res.ok) throw new Error('Failed to fetch featured post')

      const data: BlogResponse = await res.json()

      if (data.posts?.length > 0) {
        setFeaturedPost(data.posts[0])
      }
    } catch (err) {
      console.error('Error fetching featured post:', err)
    }
  }, [activeSearch])

  const fetchPosts = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    setLoading(true)
    setError(null)

    try {
      const perPage = 9
      const isFirstPage = page === 1 && !activeSearch
      const offset = isFirstPage ? 1 : (page - 1) * perPage + (activeSearch ? 0 : 1)

      let url = `${API_URL}/api/blog/posts?per_page=${perPage}&offset=${offset}`

      if (activeSearch) {
        url += `&search=${encodeURIComponent(activeSearch)}`
      }

      const res = await fetch(url, {signal: abortControllerRef.current.signal})

      if (!res.ok) throw new Error('Failed to fetch posts')

      const data: BlogResponse = await res.json()

      setPosts(data.posts || [])

      if (data.total_posts) {
        const adjustedTotal = Math.ceil((data.total_posts - 1) / perPage)
        setTotalPages(adjustedTotal)
      } else {
        setTotalPages(data.total_pages || 1)
      }

      if (!isInitialMount.current) {
        const wasSearching = previousSearch.current !== ''
        const isSearching = activeSearch !== ''

        if (isSearching || (wasSearching && !isSearching)) {
          // Scroll to top when searching or clearing search
          window.scrollTo({top: 0, behavior: 'smooth'})
        } else if (recentPostsRef.current) {
          // Scroll to recent posts section for pagination
          recentPostsRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
        }
      }
      previousSearch.current = activeSearch
      isInitialMount.current = false
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return

      console.error('Error fetching blog posts:', err)
      setError(err instanceof Error ? err.message : 'Failed to load blog posts')
    } finally {
      setLoading(false)
    }
  }, [page, activeSearch])

  useEffect(() => {
    fetchFeaturedPost()
  }, [fetchFeaturedPost])

  useEffect(() => {
    setPage(1) // Reset to page 1 when search changes
  }, [activeSearch])

  useEffect(() => {
    fetchPosts()

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchPosts])

  useEffect(() => {
    if (!activeSearch) {
      setShowFloatingBack(false)
      return
    }

    const handleScroll = () => {
      const blogHeader = document.querySelector('.blog-header')
      if (blogHeader) {
        const rect = blogHeader.getBoundingClientRect()
        const shouldShow = rect.top < 0

        if (shouldShow && !showFloatingBack) {
          setShowFloatingBack(true)
          setIsExiting(false)
        } else if (!shouldShow && showFloatingBack) {
          setIsExiting(true)
          setTimeout(() => setShowFloatingBack(false), 300)
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [activeSearch, showFloatingBack])

  const pageNumbers = useMemo(() => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({length: totalPages}, (_, i) => i + 1)
    }

    const pages: (number | string)[] = [1]
    const leftSiblingIndex = Math.max(page - 1, 2)
    const rightSiblingIndex = Math.min(page + 1, totalPages - 1)

    if (leftSiblingIndex > 2) pages.push('...')

    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
      pages.push(i)
    }

    if (rightSiblingIndex < totalPages - 1) pages.push('...')
    if (totalPages > 1) pages.push(totalPages)

    return pages
  }, [page, totalPages])

  const renderPost = useCallback(
    (post: BlogPost) => (
      <div key={post.slug} className='blog-card-wrapper'>
        <Link
          to={`${ROUTES.RESOURCE.BLOG}/${post.slug}`}
          className='blog-card card hover-elevate-up'
          aria-label={`Read article: ${sanitizeHTML(post.title, false)}`}
        >
          {post.featured_image && (
            <img
              src={post.featured_image}
              srcSet={`${post.featured_image}?w=400 400w, ${post.featured_image}?w=600 600w`}
              sizes='(max-width: 768px) 100vw, 400px'
              className='blog-card-image card-img-top'
              alt={post.title}
              loading='lazy'
            />
          )}
          <div className='blog-card-body card-body'>
            <h5 className='blog-card-title card-title fw-bold text-dark'>
              {sanitizeHTML(post.title, false)}
            </h5>
            {post.date && (
              <p className='blog-card-date text-muted fst-italic'>{formatDate(post.date)}</p>
            )}
          </div>
        </Link>
      </div>
    ),
    []
  )

  if (loading && posts.length === 0) {
    return (
      <div className='blog-loading'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>{intl.formatMessage({id: 'LOADING'})}</span>
        </div>
      </div>
    )
  }

  if (error && posts.length === 0) {
    return (
      <div className='alert alert-danger text-center' role='alert'>
        {error}
      </div>
    )
  }

  return (
    <>
      {showFloatingBack && activeSearch && (
        <button
          className={`blog-floating-back-btn ${isExiting ? 'exiting' : ''}`}
          onClick={() => navigate(ROUTES.RESOURCE.BLOG)}
          aria-label='Back to blog'
        >
          <KTSVG path='/media/gori/left-chevron-white.svg' svgClassName='h-20px w-20px' />
        </button>
      )}
      {featuredPost && !activeSearch && (
        <div className='blog-featured-wrapper'>
          <div className='blog-featured-container'>
            <Link
              to={`${ROUTES.RESOURCE.BLOG}/${featuredPost.slug}`}
              className='text-decoration-none'
            >
              <div className='row align-items-center blog-featured-row'>
                <div className='col-12 col-md-7'>
                  {featuredPost.featured_image && (
                    <div className='blog-featured-image-wrapper'>
                      <img
                        src={featuredPost.featured_image}
                        className='blog-featured-image'
                        alt={featuredPost.title}
                        loading='lazy'
                      />
                    </div>
                  )}
                </div>
                <div className='col-12 col-md-5'>
                  <div className='d-flex flex-column justify-content-center'>
                    <h2 className='blog-featured-title'>
                      {sanitizeHTML(featuredPost.title, false)}
                    </h2>
                    {featuredPost.date && (
                      <p className='blog-featured-date'>{formatDate(featuredPost.date)}</p>
                    )}
                    {featuredPost.excerpt && (
                      <div
                        className='blog-featured-excerpt'
                        dangerouslySetInnerHTML={{__html: sanitizeHTML(featuredPost.excerpt)}}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      <div className='blog-recent-title-wrapper' ref={recentPostsRef}>
        <h3 className='blog-recent-title'>
          {activeSearch
            ? intl.formatMessage({id: 'SEARCH_RESULTS'})
            : intl.formatMessage({id: 'RECENT_POSTS'})}
        </h3>
      </div>

      {!loading && posts.length === 0 && activeSearch && (
        <div className='text-center blog-no-results'>
          <p className='text-muted fs-4'>{intl.formatMessage({id: 'NO_RESULTS_FOUND'})}</p>
        </div>
      )}

      <div className='blog-grid' style={{position: 'relative'}}>
        {loading && posts.length > 0 && (
          <div className='blog-loading-overlay'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>{intl.formatMessage({id: 'LOADING'})}</span>
            </div>
          </div>
        )}
        {posts.map(renderPost)}
      </div>

      {totalPages > 1 && (
        <nav className='d-flex justify-content-center blog-pagination' aria-label='Blog pagination'>
          <ul className='pagination'>
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(1)}
                disabled={page === 1}
                aria-label='First page'
              >
                <KTSVG path='/media/gori/left-double-chevron.svg' className='svg-icon-3' />
              </button>
            </li>
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                aria-label='Previous page'
              >
                {intl.formatMessage({id: 'PREVIOUS'})}
              </button>
            </li>
            {pageNumbers.map((pageNum, idx) =>
              pageNum === '...' ? (
                <li key={`ellipsis-${idx}`} className='page-item disabled'>
                  <span className='page-link'>...</span>
                </li>
              ) : (
                <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => setPage(pageNum as number)}
                    aria-label={`Page ${pageNum}`}
                    aria-current={page === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                </li>
              )
            )}
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                aria-label='Next page'
              >
                {intl.formatMessage({id: 'NEXT'})}
              </button>
            </li>
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                aria-label='Last page'
              >
                <KTSVG path='/media/gori/right-double-chevron.svg' className='svg-icon-3' />
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  )
}
